// src/config/api.config.ts

/**
 * API Configuration for AVEC Admin Dashboard
 * Contains base URLs, endpoints, and configuration constants
 */

export const API_CONFIG = {
  // Base API URL - Force production API usage
  BASE_URL: import.meta.env.VITE_API_BASE_URL 
    ? `${import.meta.env.VITE_API_BASE_URL}/api/v1`
    : (import.meta.env.PROD 
        ? 'https://api.avec.jiigy.com/api/v1'
        : '/api/v1'), // Fallback to proxy only if no VITE_API_BASE_URL is set

  // Default tenant for multi-tenant requests
  DEFAULT_TENANT: import.meta.env.VITE_DEFAULT_TENANT || 'root',

  // Request timeout in milliseconds
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),

  // Auth endpoints
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify',
    PROFILE: '/auth/profile',
    CHANGE_PASSWORD: '/auth/change-password',
    RESET_PIN: '/auth/pin/reset',
  },

  // Member endpoints
  MEMBERS: {
    BASE: '/members',
    BY_ID: (id: string) => `/members/${id}`,
    SEARCH: '/members/search',
    WITH_PHOTO: '/members/with-photo',
    PROFILE_PHOTO: (memberId: string) => `/uploads/member/${memberId}/profile-photo`,
    BY_COMMUNE: (communeId: string) => `/communes/${communeId}/members`,
  },


  // Savings endpoints (replace existing SAVINGS section)
  SAVINGS: {
    BASE: '/savings',
    TRANSACTIONS: '/savings',
    BY_MEMBER: (memberId: string) => `/savings/member/${memberId}`,
    APPROVE: (id: string) => `/savings/${id}/approve`,
    REJECT: (id: string) => `/savings/${id}/reject`,
    CANCEL: (id: string) => `/savings/${id}/cancel`,
    STATS: '/savings/stats',
    EXPORT: '/savings/export',
  },

  // Credits endpoints
  CREDITS: {
    BASE: '/credits',
    BY_ID: (id: string) => `/credits/${id}`,
    BY_MEMBER: (memberId: string) => `/credits/member/${memberId}`,
    APPROVE: (id: string) => `/credits/${id}/approve`,
    REJECT: (id: string) => `/credits/${id}/reject`,
    DISBURSE: (id: string) => `/credits/${id}/disburse`,
    STATS: '/credits/stats',
    EXPORT: '/credits/export',
  },
  // Reports endpoints
  REPORTS: {
    BASE: '/reports',
    DASHBOARD: '/reports/dashboard',
    INCOME_STATEMENT: '/reports/income-statement',
    CREDITS_PORTFOLIO: '/reports/credits-portfolio',
    FINANCIAL: '/reports/financial',
    MEMBERS: '/reports/members',
    PERFORMANCE: '/reports/performance',
  },

  // Meetings endpoints
  MEETINGS: {
    BASE: '/meetings',
    BY_ID: (id: string) => `/meetings/${id}`,
    ATTENDANCE: (id: string) => `/meetings/${id}/attendance`,
  },

  // Staff endpoints
  STAFF: {
    BASE: '/staff',
    BY_ID: (id: string) => `/staff/${id}`,
    REGISTER: '/auth/register/staff',
    SEARCH: '/staff/search',
    ACTIVATE: (id: string) => `/staff/${id}/activate`,
    DEACTIVATE: (id: string) => `/staff/${id}/deactivate`,
    STATS: '/staff/statistics',
  },

  // Endpoints de paiements (NOUVEAU)
  PAYMENTS: {
    BASE: '/payments',
    BY_ID: (id: string) => `/payments/${id}`,
    APPROVE: (id: string) => `/payments/${id}/approve`,
    VERIFY: (id: string) => `/payments/${id}/verify`,
    RETRY: (id: string) => `/payments/${id}/retry`,
    CANCEL: (id: string) => `/payments/${id}/cancel`,
    STATS: '/payments/stats',
    EXPORT: '/payments/export',
  },
  // Settings endpoints
  SETTINGS: {
    BASE: '/settings',
    CATEGORIES: '/settings/categories',
    BY_KEY: (key: string) => `/settings/${key}`,
    UPDATE_BY_KEY: (key: string) => `/settings/${key}/`,
  },

  ACCOUNTS: {
    BASE: '/accounts',
    BY_MEMBER: (memberId: string) => `/accounts/member/${memberId}`,
  },

  // Commune endpoints (NEW)
  COMMUNES: {
    BASE: '/communes',
    BY_ID: (id: string) => `/communes/${id}`,
    ASSIGN_ADMIN: (communeId: string) => `/communes/${communeId}/assign-admin`,
    GROUPS: (communeId: string) => `/communes/${communeId}/groups`,
    VILLAGES: (communeId: string) => `/communes/${communeId}/villages`,
    STATISTICS: (communeId: string) => `/communes/${communeId}/statistics`,
    DASHBOARD: (communeId: string) => `/communes/${communeId}/dashboard`,
    CONTRIBUTIONS_STATISTICS: (communeId: string) => `/communes/${communeId}/contributions/statistics`,
    SOLIDARITY_REQUESTS: (communeId: string) => `/communes/${communeId}/solidarity-requests`,
  },

  // Province endpoints (NEW)
  PROVINCES: {
    BASE: '/provinces',
    BY_ID: (id: string) => `/provinces/${id}`,
    COMMUNES: (provinceId: string) => `/provinces/${provinceId}/communes`,
    VILLAGES: (provinceId: string) => `/provinces/${provinceId}/villages`,
    GROUPS: (provinceId: string) => `/provinces/${provinceId}/groups`,
    STATISTICS: (provinceId: string) => `/provinces/${provinceId}/statistics`,
    DASHBOARD: (provinceId: string) => `/provinces/${provinceId}/dashboard`,
  },

  // Region endpoints (NEW)
  REGIONS: {
    BASE: '/regions',
    BY_ID: (id: string) => `/regions/${id}`,
    PROVINCES: (regionId: string) => `/regions/${regionId}/provinces`,
    COMMUNES: (regionId: string) => `/regions/${regionId}/communes`,
    VILLAGES: (regionId: string) => `/regions/${regionId}/villages`,
    GROUPS: (regionId: string) => `/regions/${regionId}/groups`,
    STATISTICS: (regionId: string) => `/regions/${regionId}/statistics`,
    DASHBOARD: (regionId: string) => `/regions/${regionId}/dashboard`,
  },

  // Villages endpoints
  VILLAGES: {
    BASE: '/villages',
    BY_ID: (id: string) => `/villages/${id}`,
    GROUPS: (villageId: string) => `/villages/${villageId}/groups`,
    MEMBERS: (villageId: string) => `/villages/${villageId}/members`,
    STATISTICS: (villageId: string) => `/villages/${villageId}/statistics`,
    DASHBOARD: (villageId: string) => `/villages/${villageId}/dashboard`,
  },

  // Memberships endpoints (standalone)
  MEMBERSHIPS: {
    BASE: '/memberships',
    BY_ID: (id: string) => `/memberships/${id}`,
    APPROVE: (membershipId: string) => `/memberships/${membershipId}/approve`,
    REJECT: (membershipId: string) => `/memberships/${membershipId}/reject`,
    SUSPEND: (membershipId: string) => `/memberships/${membershipId}/suspend`,
    REACTIVATE: (membershipId: string) => `/memberships/${membershipId}/reactivate`,
    UPDATE_ROLE: (membershipId: string) => `/memberships/${membershipId}`,
    REMOVE: (membershipId: string) => `/memberships/${membershipId}`,
    TRANSFER: (membershipId: string) => `/memberships/${membershipId}/transfer`,
    UPDATE_MEMBER: (membershipId: string) => `/memberships/${membershipId}/member`,
  },

  // Group endpoints (NEW)
  GROUPS: {
    BASE: '/groups',
    BY_ID: (id: string) => `/groups/${id}`,
    CREATE: '/groups',
    UPDATE: (id: string) => `/groups/${id}`,
    DELETE: (id: string) => `/groups/${id}`,
    DASHBOARD: (id: string) => `/groups/${id}/dashboard`,
    FINANCIAL_SUMMARY: (id: string) => `/groups/${id}/financial-summary`,
    STATISTICS: (id: string) => `/groups/${id}/statistics`,
    SETTINGS: (id: string) => `/groups/${id}/settings`,
    START_CYCLE: (id: string) => `/groups/${id}/start-cycle`,
    SUSPEND: (id: string) => `/groups/${id}/suspend`,
    REACTIVATE: (id: string) => `/groups/${id}/reactivate`,
    
    // Group member management
    MEMBERS: (id: string) => `/groups/${id}/members`,
    MEMBERSHIPS: (id: string) => `/groups/${id}/memberships`,
    ADD_MEMBER: (id: string) => `/groups/${id}/members`,
    APPROVE_MEMBERSHIP: (groupId: string, membershipId: string) => `/groups/${groupId}/memberships/${membershipId}/approve`,
    
    // Group credits
    CREDITS: (id: string) => `/groups/${id}/credits`,
    CREDIT_REQUEST: (id: string) => `/groups/${id}/credits/request`,
    APPROVE_CREDIT: (groupId: string, creditId: string) => `/groups/${groupId}/credits/${creditId}/approve`,
    DISBURSE_CREDIT: (groupId: string, creditId: string) => `/groups/${groupId}/credits/${creditId}/disburse`,
    
    // Group solidarity
    SOLIDARITY_REQUESTS: (id: string) => `/groups/${id}/solidarity/requests`,
    SUBMIT_SOLIDARITY: (id: string) => `/groups/${id}/solidarity/requests`,
    APPROVE_SOLIDARITY: (groupId: string, requestId: string) => `/groups/${groupId}/solidarity/requests/${requestId}/approve`,
    DISBURSE_SOLIDARITY: (groupId: string, requestId: string) => `/groups/${groupId}/solidarity/requests/${requestId}/disburse`,
    SOLIDARITY_FUND_STATUS: (id: string) => `/groups/${id}/solidarity/fund-status`,
    
    // Group contributions  
    WEEKLY_CONTRIBUTIONS: (id: string) => `/groups/${id}/contributions`,
    WEEKLY_CONTRIBUTIONS_GENERATE: (id: string) => `/groups/${id}/contributions/generate`,
    WEEKLY_CONTRIBUTIONS_WEEK: (id: string, week: string) => `/groups/${id}/contributions/week/${week}`,
    WEEKLY_CONTRIBUTIONS_OVERDUE: (id: string) => `/groups/${id}/contributions/overdue`,
    WEEKLY_CONTRIBUTIONS_PENDING: (id: string) => `/groups/${id}/contributions/pending`,
    WEEKLY_CONTRIBUTIONS_STATISTICS: (id: string) => `/groups/${id}/contributions/statistics`,
    WEEKLY_CONTRIBUTIONS_COLLECTION_SUMMARY: (id: string) => `/groups/${id}/contributions/collection-summary`,
    WEEKLY_CONTRIBUTIONS_BULK_UPDATE: (id: string) => `/groups/${id}/contributions/bulk-update`,
    WEEKLY_CONTRIBUTIONS_QUICK_RECORD: (id: string) => `/groups/${id}/contributions/quick-record`,
    WEEKLY_CONTRIBUTIONS_SEND_REMINDERS: (id: string, week: string) => `/groups/${id}/contributions/week/${week}/send-reminders`,
    
    SOLIDARITY_CONTRIBUTIONS: (id: string) => `/groups/${id}/solidarity-contributions`,
    CONTRIBUTIONS_STATISTICS: (id: string) => `/groups/${id}/contributions/statistics`,
    
    // Group agent management
    AGENT: (id: string) => `/groups/${id}/agent`,
    ASSIGN_AGENT: (id: string) => `/groups/${id}/assign-agent`,
    REMOVE_AGENT: (id: string) => `/groups/${id}/remove-agent`,
    CHECK_AGENT_PERMISSION: (id: string) => `/groups/${id}/check-agent-permission`,
  },

  // Weekly Contributions endpoints (individual)
  WEEKLY_CONTRIBUTIONS: {
    BASE: '/contributions',
    BY_ID: (id: string) => `/contributions/${id}`,
    RECORD: (id: string) => `/contributions/${id}/record`,
    MARK_MISSED: (id: string) => `/contributions/${id}/mark-missed`,
    WAIVE: (id: string) => `/contributions/${id}/waive`,
    MEMBER_HISTORY: (memberId: string) => `/members/${memberId}/contributions/history`,
    OVERDUE: '/contributions/overdue',
    WEEK_GLOBAL: (week: string) => `/contributions/week/${week}`,
    EXPORT: '/contributions/export',
  },

} as const;
export const REQUEST_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
} as const;

export default API_CONFIG;